import React, { useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { Modal, ModalBody, ModalFooter, Button } from "reactstrap";

import "./assets/plugins/nucleo/css/nucleo.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./assets/scss/argon-dashboard-react.scss";

import CadastroAluno from "./views/CadastroAluno";
import AdminLayout from "./layouts/Admin/Admin";
import AuthLayout from "./layouts/Auth/Auth";
import AuthContextProvider, { AuthContext } from "./context/AuthContext";
import { ToastProvider } from "./context/ToastContext";
import LoadingContextProvider from "./context/LoadingContext";
import AlunoContextProvider from "./context/AlunoContext";
import InstituicaoContextProvider from "./context/InstituicaoContext";
import EmpresaContextProvider from "./context/EmpresaContext";
import UserTypeContextProvider from "./context/UserTypeContext";
import TerceiroContextProvider from "./context/TerceiroContext";
import TipoTerceiroContextProvider from "./context/TipoTerceiro";
import ContratoContextProvider from "./context/ContratoContext";
import CursoContextProvider from "./context/CursoContext";
import VendedorContextProvider from "./context/VendedorContext";
import ResponsavelContextProvider from "./context/ResponsavelContext";
import TipoCustoContextProvider from "./context/TipoCustoContext";
import CustoContextProvider from "./context/CustoContext";
import TipoAgendaContextProvider from "./context/TipoAgendaContext";
import AgendaContextProvider from "./context/AgendaContext";
import PreparacaoAlbumContextProvider from "./context/PreparacaoAlbumContext";
import CategoriaFinanceiraContextProvider from "./context/CategoriaFinanceiraContext";
import ContaFinanceiraContextProvider from "./context/ContaFinanceiraContext";
import FluxoCaixaContextProvider from "./context/FluxoCaixaContext";
import RelatorioVendaContextProvider from "./context/RelatorioVendaContext";
import ContratoStatusReportContextProvider from "./context/ContratoStatusReportContext";
import ObservacaoContratoContextProvider from "./context/ObservacaoContratoContext";
import ContaPagarContextProvider from "./context/ContaPagarContext";
import ContaReceberContextProvider from "./context/ContaReceberContext";
import FormaPagamentoContextProvider from "./context/FormaPagamentoContext";
import ProdutoContextProvider from "./context/ProdutoContext";
import DespesasVendedorContextProvider from "./context/DespesasVendedorContext";
import DespesasFotografoContextProvider from "./context/DespesasFotografoContext";
import TipoDespesasVendedorContextProvider from "./context/TipoDespesasVendedorContext";
import ContaProvisionadaContextProvider from "./context/ContaProvisionadaContext";
import TipoContaPagarContextProvider from "./context/TipoContaPagarContext";
import GerenteFotografoContextProvider from "./context/GerenteFotografoContext";
import TipoServicoContextProvider from "./context/TipoServicoContext";

let appRoot: ReactDOM.Root | null = null;
let fatalShown = false;

function ensureRootElement() {
  let el = document.getElementById("root");
  if (!el) {
    el = document.createElement("div");
    el.id = "root";
    document.body.appendChild(el);
  }
  return el;
}

function ensureFatalElement() {
  let el = document.getElementById("__fatal");
  if (!el) {
    el = document.createElement("div");
    el.id = "__fatal";
    el.style.position = "fixed";
    el.style.inset = "0";
    el.style.background = "#fff";
    el.style.zIndex = "999999";
    el.style.display = "none";
    document.body.appendChild(el);
  }
  return el;
}

function renderFatalScreen(title: string, message: string) {
  if (fatalShown) return;
  fatalShown = true;

  try {
    appRoot?.unmount();
  } catch { }

  const el = ensureFatalElement();
  el.style.display = "block";

  el.innerHTML = `
    <div style="padding:24px;font-family:system-ui;max-width:720px;margin:0 auto">
      <h2 style="margin:0 0 8px 0">${title}</h2>
      <p style="margin:0 0 16px 0">${message}</p>
      <ul style="margin:0 0 16px 18px;opacity:.85">
        <li>Pressione <b>Ctrl + F5</b> para atualizar</li>
        <li>Se não resolver, abra em <b>janela anônima</b></li>
        <li>Se persistir, limpe o <b>cache</b> do navegador</li>
      </ul>
      <button id="fatal-reload-btn" style="padding:10px 14px;border-radius:8px;border:1px solid #ddd;cursor:pointer">
        Recarregar
      </button>
      <div style="margin-top:14px;font-size:12px;opacity:.7">
        Se possível, envie um print do Console (F12).
      </div>
    </div>
  `;

  const btn = document.getElementById("fatal-reload-btn");
  if (btn) btn.addEventListener("click", () => window.location.reload());
}

function isRemoveChildNotFoundError(event: any) {
  const msg = String(event?.message || event?.error?.message || "");
  const name = String(event?.error?.name || event?.name || "");
  return (
    name === "NotFoundError" &&
    (msg.includes("removeChild") || msg.includes("The node to be removed is not a child"))
  );
}

function isChunkLoadErrorMessage(msg: string) {
  return msg.includes("ChunkLoadError") || msg.includes("Loading chunk");
}

function installGlobalErrorHandlers() {
  window.addEventListener("error", (event: any) => {
    console.error("window.error:", event?.error || event);

    if (isRemoveChildNotFoundError(event)) {
      return;
    }

    const msg = String(event?.message || event?.error?.message || "");

    if (isChunkLoadErrorMessage(msg)) {
      renderFatalScreen(
        "Atualização do sistema pendente",
        "Seu navegador carregou uma versão antiga do sistema. Vamos recarregar para atualizar."
      );
      setTimeout(() => window.location.reload(), 800);
      return;
    }

    renderFatalScreen(
      "Não foi possível abrir o sistema",
      "Ocorreu um erro ao carregar o aplicativo. Tente atualizar a página."
    );
  });

  window.addEventListener("unhandledrejection", (event: any) => {
    console.error("unhandledrejection:", event?.reason || event);

    const reasonMsg = String(event?.reason?.message || event?.reason || "");

    if (reasonMsg.includes("NotFoundError") && reasonMsg.includes("removeChild")) {
      return;
    }

    if (isChunkLoadErrorMessage(reasonMsg)) {
      renderFatalScreen(
        "Atualização do sistema pendente",
        "Seu navegador está com arquivos antigos. Vamos recarregar."
      );
      setTimeout(() => window.location.reload(), 800);
      return;
    }

    renderFatalScreen(
      "Não foi possível abrir o sistema",
      "Ocorreu um erro ao carregar o aplicativo. Tente atualizar a página."
    );
  });
}

installGlobalErrorHandlers();

const rootElement = ensureRootElement();
appRoot = ReactDOM.createRoot(rootElement);

function CustomRoute() {
  const { authenticated, loadingButton, handleLogout } = useContext(AuthContext);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [swRegistration, setSwRegistration] =
    useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    const configuration = {
      onUpdate: (registration: ServiceWorkerRegistration) => {
        if (registration?.waiting) {
          setSwRegistration(registration);
          setShowUpdateModal(true);
        }
      },
    };

    serviceWorkerRegistration.register(configuration);
  }, []);

  if (loadingButton) {
    return <div style={{ padding: 24 }}>Carregando...</div>;
  }

  const handleUpdateConfirmation = () => {
    let reloaded = false;

    const onControllerChange = () => {
      if (reloaded) return;
      reloaded = true;
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        onControllerChange
      );
      window.location.reload();
    };

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        onControllerChange
      );
    }

    try {
      swRegistration?.waiting?.postMessage({ type: "SKIP_WAITING" });
    } catch (e) {
      console.error("Falha ao solicitar SKIP_WAITING:", e);
    }

    setTimeout(() => window.location.reload(), 1500);

    try {
      handleLogout();
    } catch { }
  };

  const UpdateModal = (
    <Modal isOpen={showUpdateModal}>
      <ModalBody>
        Uma nova versão do aplicativo está disponível. Deseja recarregar a página
        para aplicar as alterações?
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={handleUpdateConfirmation}>
          OK
        </Button>
      </ModalFooter>
    </Modal>
  );

  if (!authenticated) {
    return (
      <>
        <Routes>
          <Route path="/auth/*" element={<AuthLayout />} />
          <Route
            path="/cadastro-aluno/:contratoCursoGuid"
            element={<CadastroAluno />}
          />
          <Route path="*" element={<Navigate to="/auth/" replace />} />
        </Routes>
        {UpdateModal}
      </>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/admin/*" element={<AdminLayout />} />
        <Route
          path="/cadastro-aluno/:encryptedContratoId"
          element={<CadastroAluno />}
        />
        <Route path="*" element={<Navigate to="/admin/" replace />} />
      </Routes>
      {UpdateModal}
    </>
  );
}

appRoot.render(
  <BrowserRouter>
    <ToastProvider>
      <LoadingContextProvider>
        <AuthContextProvider>
          <EmpresaContextProvider>
            <UserTypeContextProvider>
              <InstituicaoContextProvider>
                <ResponsavelContextProvider>
                  <CursoContextProvider>
                    <VendedorContextProvider>
                      <AlunoContextProvider>
                        <TipoTerceiroContextProvider>
                          <TerceiroContextProvider>
                            <TipoAgendaContextProvider>
                              <AgendaContextProvider>
                                <ObservacaoContratoContextProvider>
                                  <TipoCustoContextProvider>
                                    <CustoContextProvider>
                                      <ContratoContextProvider>
                                        <PreparacaoAlbumContextProvider>
                                          <RelatorioVendaContextProvider>
                                            <ContratoStatusReportContextProvider>
                                            <CategoriaFinanceiraContextProvider>
                                              <TipoContaPagarContextProvider>
                                                <ContaFinanceiraContextProvider>
                                                  <ContaPagarContextProvider>
                                                    <ContaReceberContextProvider>
                                                      <ContaProvisionadaContextProvider>
                                                        <FluxoCaixaContextProvider>
                                                          <FormaPagamentoContextProvider>
                                                            <ProdutoContextProvider>
                                                              <DespesasVendedorContextProvider>
                                                                <DespesasFotografoContextProvider>
                                                                  <TipoDespesasVendedorContextProvider>
                                                                    <GerenteFotografoContextProvider>
                                                                      <TipoServicoContextProvider>
                                                                        <CustomRoute />
                                                                      </TipoServicoContextProvider>
                                                                    </GerenteFotografoContextProvider>
                                                                  </TipoDespesasVendedorContextProvider>
                                                                </DespesasFotografoContextProvider>
                                                              </DespesasVendedorContextProvider>
                                                            </ProdutoContextProvider>
                                                          </FormaPagamentoContextProvider>
                                                        </FluxoCaixaContextProvider>
                                                      </ContaProvisionadaContextProvider>
                                                    </ContaReceberContextProvider>
                                                  </ContaPagarContextProvider>
                                                </ContaFinanceiraContextProvider>
                                              </TipoContaPagarContextProvider>
                                            </CategoriaFinanceiraContextProvider>
                                            </ContratoStatusReportContextProvider>
                                          </RelatorioVendaContextProvider>
                                        </PreparacaoAlbumContextProvider>
                                      </ContratoContextProvider>
                                    </CustoContextProvider>
                                  </TipoCustoContextProvider>
                                </ObservacaoContratoContextProvider>
                              </AgendaContextProvider>
                            </TipoAgendaContextProvider>
                          </TerceiroContextProvider>
                        </TipoTerceiroContextProvider>
                      </AlunoContextProvider>
                    </VendedorContextProvider>
                  </CursoContextProvider>
                </ResponsavelContextProvider>
              </InstituicaoContextProvider>
            </UserTypeContextProvider>
          </EmpresaContextProvider>
        </AuthContextProvider>
      </LoadingContextProvider>
    </ToastProvider>
  </BrowserRouter>
);